import { type Page_recommendationsUsername_page$key } from '#app/__generated__/relay/Page_recommendationsUsername_page.graphql.js';
import { type Page_recommendationsUsernameQuery } from '#app/__generated__/relay/Page_recommendationsUsernameQuery.graphql.js';
import { NotFound } from '#app/components/NotFound/NotFound.js';
import { PortfolioRedirect } from '#app/components/PortfolioRedirect/PortfolioRedirect.js';
import { Redirect } from '#app/components/Redirect/index.js';
import { IneligibleForRequestRecommendationCard } from '#app/components/RequestRecommendation/IneligibleForRequestRecommendationCard.js';
import { RequestedRecommendations } from '#app/components/RequestRecommendation/RequestedRecommendations.js';
import { SSRSafeRender } from '#app/components/SSRSafeRender/SSRSafeRender.js';
import { UserReviews } from '#app/components/UserReview/UserReviews.js';
import { CreateCard } from '#app/features/profile/components/CreateCard/CreateCard.js';
import { ProfileLayout } from '#app/features/profile/layouts/ProfileLayout.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useRegisterAnalyticEvent } from '#app/hooks/useRegisterAnalyticEvent.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { type PageProps } from '#app/types/router.js';
import { graphql, useFragment, usePreloadedQuery } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { Helmet } from 'react-helmet-async';
import { styled } from 'styled-components';

type ProfileRecommendationsPageProps = PageProps<
  '/@username',
  Page_recommendationsUsernameQuery
> & {
  readonly preloaded: { isFilteringRoute?: boolean };
};

const CreateCardContainer = styled.div`
  margin-bottom: 48px;
`;

const HybridPage = ({ preloaded }: ProfileRecommendationsPageProps) => {
  const data = usePreloadedQuery<Page_recommendationsUsernameQuery>(
    graphql`
      query Page_recommendationsUsernameQuery($username: String!) {
        visitor {
          id
          userAccount {
            isEligibleForRequestRecommendations
            ...ProfileLayout_userAccount
          }
        }
        userProfileByUsername(username: $username) {
          id
          ...Page_recommendationsUsername_page
        }
      }
    `,
    preloaded,
  );

  const { userProfileByUsername, visitor } = data;
  const { track } = useAnalytics();

  const { isContractor } = useSelectedUserType();

  const profile = useFragment<Page_recommendationsUsername_page$key>(
    graphql`
      fragment Page_recommendationsUsername_page on UserProfile {
        id
        firstName
        lastName
        visitorCanEdit
        displayUsername
        receivedTestimonials {
          edges {
            node {
              id
              recommend
            }
          }
        }
        recommendationRequests {
          count
        }
        ...ProfileLayout_userProfile
        ...RequestedRecommendations_userProfile
        ...UserReviews_userProfile
      }
    `,
    userProfileByUsername,
  );

  const [urlSearchParameters] = useURLParameters('/@username/recommendations');
  const redirectToPortfolio = urlSearchParameters.redirectToPortfolio;

  useRegisterAnalyticEvent(
    profile && !profile.visitorCanEdit
      ? {
          eventType: 'UserProfileView',
          input: { userProfileId: profile.id },
        }
      : undefined,
  );

  const positiveRecommendations =
    profile?.receivedTestimonials?.edges?.filter?.(
      ({ node: { recommend } }) => recommend,
    ) ?? [];

  const userHasRecommendations = Boolean(
    positiveRecommendations.length +
      (profile?.recommendationRequests.count ?? 0),
  );

  const shouldBeVisibleToOwnerOnly =
    !userHasRecommendations && !profile?.visitorCanEdit;

  if (shouldBeVisibleToOwnerOnly) {
    return (
      <Redirect
        method="replace"
        to={
          profile?.displayUsername
            ? getRoute('/@username', {
                args: { username: profile.displayUsername },
              })
            : getRoute('/')
        }
      />
    );
  }

  if (!profile) return <NotFound />;

  if (redirectToPortfolio) {
    return (
      <PortfolioRedirect
        displayUsername={profile.displayUsername}
        route="userRecommendations"
      />
    );
  }

  const requestReviewsSection = (() => {
    if (!(profile.visitorCanEdit && isContractor)) {
      return null;
    }

    if (
      !visitor?.userAccount?.isEligibleForRequestRecommendations &&
      userHasRecommendations
    ) {
      return null;
    }

    if (!visitor?.userAccount?.isEligibleForRequestRecommendations) {
      return (
        <CreateCardContainer>
          <IneligibleForRequestRecommendationCard />
        </CreateCardContainer>
      );
    }

    return (
      <>
        <CreateCardContainer>
          <CreateCard
            onCreateClick={() => {
              track('button', {
                action: 'tapped',
                button_name: 'growth_recommendations_request_cta_primary',
                section: 'relationship_page',
              });
            }}
            variant="recommendations"
          />
        </CreateCardContainer>
        <RequestedRecommendations profileRef={profile} />
      </>
    );
  })();

  return (
    <>
      <Helmet>
        <title>{`Recommendations received by ${profile.firstName} ${profile.lastName}`}</title>
      </Helmet>

      <ProfileLayout
        accountRef={visitor.userAccount}
        userProfileRef={profile}
      >
        {requestReviewsSection}

        {profile.visitorCanEdit ? null : (
          // approved requested recommendations should be visible to other users
          <RequestedRecommendations profileRef={profile} />
        )}

        <UserReviews profileKey={profile} />
      </ProfileLayout>
    </>
  );
};

export const Page = ({ preloaded }: ProfileRecommendationsPageProps) => {
  const currentUser = useCurrentUser();

  return (
    <SSRSafeRender skipServerRender={Boolean(currentUser)}>
      <HybridPage preloaded={preloaded} />
    </SSRSafeRender>
  );
};
